import {
  ChapterInfo,
  ErgRow,
  MatrixCellData,
  MetricData,
  OmniVisibleRow,
  ResolvedCellDisplay,
  SiteColumn,
  SummaryEntry,
} from '../omnidashboard.models';
import { OMNI_STRINGS, OmniViewMode } from '../omnidashboard.config';
import { OmniFilterState } from '../filters/omnidashboard-filters.model';
import { resolveOmniFilters } from '../filters/omnidashboard-filters';
import {
  buildMatrixCellMap,
  buildMatrixGrid,
  buildResolvedGrid,
  buildSummary,
  buildVisibleRows,
  countVisibleChapters,
  deriveAxesFromChapters,
} from './omnidashboard-matrix';
import {
  formatDefaultCellTooltip,
  getMetricDefinitionOrThrow,
} from '../metrics/metric-registry';

export interface OmniMatrixViewModel {
  siteColumns: SiteColumn[];
  ergRows: ErgRow[];
  visibleSiteColumns: SiteColumn[];
  visibleErgRows: ErgRow[];
  visibleRows: OmniVisibleRow[];
  visibleChapterCount: number;
  summary: SummaryEntry[];
  matrixCellMap: Map<string, MatrixCellData>;
  matrixGrid: MatrixCellData[][];
  resolvedGrid: ResolvedCellDisplay[][];
}

export function createEmptyMatrixViewModel(): OmniMatrixViewModel {
  return {
    siteColumns: [],
    ergRows: [],
    visibleSiteColumns: [],
    visibleErgRows: [],
    visibleRows: [],
    visibleChapterCount: 0,
    summary: [],
    matrixCellMap: new Map(),
    matrixGrid: [],
    resolvedGrid: [],
  };
}

function getCellTooltip(
  cell: MatrixCellData,
  activeView: OmniViewMode,
  activeMetric = getMetricDefinitionOrThrow(activeView)
): string {
  if (!cell.chapterId) {
    return OMNI_STRINGS.cellNaTooltip;
  }
  const metric: MetricData = cell.metrics.get(activeMetric.key) || activeMetric.naMetric;
  if (activeMetric.tooltipFormatter) {
    return activeMetric.tooltipFormatter(cell, metric);
  }
  return formatDefaultCellTooltip(cell);
}

export function buildMatrixFromChapters(chapters: ChapterInfo[]): {
  siteColumns: SiteColumn[];
  ergRows: ErgRow[];
  matrixCellMap: Map<string, MatrixCellData>;
  matrixGrid: MatrixCellData[][];
} {
  const axes = deriveAxesFromChapters(chapters);
  const matrixCellMap = buildMatrixCellMap(chapters);
  const matrixGrid = buildMatrixGrid(axes.ergRows, axes.siteColumns, matrixCellMap);
  return {
    siteColumns: axes.siteColumns,
    ergRows: axes.ergRows,
    matrixCellMap,
    matrixGrid,
  };
}

export function buildMatrixViewModel(
  activeView: OmniViewMode,
  filters: OmniFilterState,
  siteColumns: SiteColumn[],
  ergRows: ErgRow[],
  matrixCellMap: Map<string, MatrixCellData>,
  matrixGrid: MatrixCellData[][]
): OmniMatrixViewModel {
  const activeMetric = getMetricDefinitionOrThrow(activeView);
  const tooltipResolver = (cell: MatrixCellData) => getCellTooltip(cell, activeView, activeMetric);
  const resolvedGrid = buildResolvedGrid(matrixGrid, activeMetric, tooltipResolver);
  const resolvedFilters = resolveOmniFilters(filters, siteColumns, ergRows, 0);

  const visibleRows = buildVisibleRows(
    ergRows,
    siteColumns,
    resolvedFilters.visibleErgRows,
    resolvedFilters.visibleSiteColumns,
    matrixGrid,
    resolvedGrid,
    matrixCellMap,
    activeMetric,
    tooltipResolver
  );
  const visibleChapterCount = countVisibleChapters(visibleRows);

  const summary = resolvedFilters.hasNoVisibleMatrix
    ? []
    : buildSummary(activeMetric, visibleRows, resolvedFilters.visibleErgRows, resolvedFilters.visibleSiteColumns);

  return {
    siteColumns,
    ergRows,
    visibleSiteColumns: resolvedFilters.visibleSiteColumns,
    visibleErgRows: resolvedFilters.visibleErgRows,
    visibleRows,
    visibleChapterCount,
    summary,
    matrixCellMap,
    matrixGrid,
    resolvedGrid,
  };
}

export function applyMetricToAllCells(
  matrixCellMap: Map<string, MatrixCellData>,
  metricName: string,
  resolver: (cell: MatrixCellData) => MetricData
): Map<string, MatrixCellData> {
  // Returns new map/cell references so OnPush consumers detect the update.
  const next = new Map<string, MatrixCellData>();
  matrixCellMap.forEach((cell, key) => {
    const metrics = new Map(cell.metrics);
    metrics.set(metricName, resolver(cell));
    next.set(key, { ...cell, metrics });
  });
  return next;
}
